import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import {
  IModalCTACallbacks,
  IModalCTAContext,
  IValuesForModal,
} from './interfaces';
import ConfirmModal from '@components/ConfirmModal';
import ConfirmLogoutModal from '@components/ConfirmLogoutModal';

// NOTE (TASK-02, context→zustand): file name kept as *Provider for now; it no
// longer renders a React Context provider. The modal UI moved to
// <ModalCTARenderer />, mounted in App.tsx. Renaming is handled by TASK-08.

interface IModalCTAStore {
  valuesForModal: IValuesForModal | null;
  callbacks: IModalCTACallbacks;
  setValuesForModal: (values: IValuesForModal | null) => void;
  onCloseModal: () => void;
  onCancelModal: () => void;
  onConfirmModal: () => void;
  onCancelAltAction: () => void;
  resetState: () => void;
}

const initialState: Pick<IModalCTAStore, 'valuesForModal' | 'callbacks'> = {
  valuesForModal: null,
  callbacks: {},
};

export const useModalCTAStore = create<IModalCTAStore>((set, get) => ({
  ...initialState,
  setValuesForModal: (payload) => {
    if (payload === null) {
      set({ valuesForModal: null, callbacks: {} });
      return;
    }

    const { onCancel, onClose, onConfirm, onCancelAltAction, ...rest } =
      payload;

    set({
      callbacks: {
        onCancel: onCancel ?? undefined,
        onClose: onClose ?? undefined,
        onConfirm: onConfirm ?? undefined,
        onCancelAltAction: onCancelAltAction ?? undefined,
      },
      valuesForModal: rest,
    });
  },
  onCloseModal: () => {
    get().callbacks.onClose?.();
    set({ valuesForModal: null, callbacks: {} });
  },
  onCancelModal: () => {
    get().callbacks.onCancel?.();
    set({ valuesForModal: null, callbacks: {} });
  },
  onConfirmModal: () => {
    get().callbacks.onConfirm?.();
    set({ valuesForModal: null, callbacks: {} });
  },
  onCancelAltAction: () => {
    get().callbacks.onCancelAltAction?.();
    set({ valuesForModal: null, callbacks: {} });
  },
  resetState: () => set({ ...initialState }),
}));

// Backwards-compatible hook: consumers keep calling useModalCTAContext().
export const useModalCTAContext = (): IModalCTAContext => {
  const setValuesForModal = useModalCTAStore(
    (state) => state.setValuesForModal,
  );

  return { setValuesForModal };
};

// Renders the confirm modals driven by the store. Replaces the JSX the old
// <ModalCTAProvider> rendered around its children; mount once in App.tsx.
export const ModalCTARenderer = () => {
  const {
    valuesForModal,
    callbacks,
    onCloseModal,
    onCancelModal,
    onConfirmModal,
    onCancelAltAction,
  } = useModalCTAStore(
    useShallow((state) => ({
      valuesForModal: state.valuesForModal,
      callbacks: state.callbacks,
      onCloseModal: state.onCloseModal,
      onCancelModal: state.onCancelModal,
      onConfirmModal: state.onConfirmModal,
      onCancelAltAction: state.onCancelAltAction,
    })),
  );

  return (
    <>
      {valuesForModal?.isConfirmLogoutModal && (
        <ConfirmLogoutModal
          open={valuesForModal?.open || false}
          onClose={onCloseModal}
          onConfirm={onConfirmModal}
          altStyling
        />
      )}
      {!valuesForModal?.isConfirmLogoutModal && (
        <ConfirmModal
          icon={valuesForModal?.icon}
          title={valuesForModal?.title}
          message={valuesForModal?.message}
          open={valuesForModal?.open || false}
          onCancel={callbacks.onCancel && onCancelModal}
          onConfirm={callbacks.onConfirm && onConfirmModal}
          onClose={callbacks.onClose && onCloseModal}
          confirmButtonName={valuesForModal?.confirmButtonName}
          cancelButtonName={valuesForModal?.cancelButtonName}
          onCancelAltAction={callbacks.onCancelAltAction && onCancelAltAction}
          isConfirmLoading={valuesForModal?.isConfirmLoading || false}
          isCancelLoading={valuesForModal?.isCancelLoading || false}
          altStyling={valuesForModal?.altStyling || false}
          hideCloseButton={valuesForModal?.hideCloseButton || false}
          sx={valuesForModal?.sx || {}}
        >
          {valuesForModal?.children}
        </ConfirmModal>
      )}
    </>
  );
};
